import { Box } from "@mui/system";
import {
  Button,
  Card,
  Grid,
  IconButton,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  COURSE_MATERIAL_TYPE,
  DATE_OF_BIRTH_FORMAT,
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  EDUCATOR_MODULE_NAME,
} from "utils/constants/constant";
import NoRecords from "components/NoRecords";
import { getPagePermissions, hideLoader, showLoader } from "utils/helper";
import CustomPagination from "components/customPagination";
import moment from "moment";
import {
  deleteIcon,
  documentPreviewIcon,
  editIcon,
  plusLightIcon,
} from "assets/images";
import {
  ICourseMaterial,
  ICourseMaterialContainerDispatch,
  ICourseMaterialListMeta,
  ICourseMaterialPagePayloadValues,
  ICourseMaterialPreview,
} from "utils/interfaces/course";
import {
  AddEditCourseMaterialSuccessPayload,
  CourseMaterialListSuccessPayload,
  GroupedCourseMaterial,
} from "store/course/types";
import projectTheme from "app.theme";
import { DeleteDocumentSuccessPayload } from "store/document/types";
import {
  COURSE_MATERIAL_ADD_SUCCESS_MSG,
  COURSE_MATERIAL_UPDATE_SUCCESS_MSG,
  DOCUMENT_DELETE_MODAL_MSG,
  DOCUMENT_DELETE_SUCCESS_MSG,
} from "utils/constants/Messages";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AppPages } from "utils/enums/app-pages";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import { IAddEditDocumentRequest } from "utils/interfaces/document";
import { AuthState } from "store/rootReducer";
import { AuthState as AuthType } from "store/auth/types";
import { getUserSelector } from "store/auth/selector";
import AddEditCourseMaterial from "./AddEditCourseMaterial";

interface ICourseMaterialsProps extends ICourseMaterialContainerDispatch {
  courseId: number;
  isAdministrator: boolean;
}

const CourseMaterialsTable: React.FC<ICourseMaterialsProps> = (props) => {
  const user = useSelector<AuthState, AuthType["user"]>(getUserSelector);
  const history = useHistory();
  const { courseId, isAdministrator } = props;

  const [courseMaterialList, setCourseMaterialList] = useState<
    GroupedCourseMaterial[] | null
  >(null);
  const [courseMaterialListMeta, setCourseMaterialListMeta] =
    useState<ICourseMaterialListMeta>();
  const [page, setPage] = useState<string>(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [isEditCourseMaterial, setIsEditCourseMaterial] =
    useState<boolean>(false);
  const [editData, setEditData] = useState<ICourseMaterial>();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenDocumentDeleteModal, setIsOpenDocumentDeleteModal] =
    useState<boolean>(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<number>();

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(DEFAULT_PAGE_NO);
  };

  const onCourseMaterialListSuccess = (
    response: CourseMaterialListSuccessPayload
  ) => {
    hideLoader();
    setCourseMaterialList(response.groupedCourseMaterialList);
    setCourseMaterialListMeta(response.courseMaterialListPagination);
  };

  const getCourseMaterialList = () => {
    const { courseMaterialListRequest } = props;
    if (courseMaterialListRequest && courseId) {
      showLoader();
      const values: ICourseMaterialPagePayloadValues = {
        pageNo,
        pageSize: Number(page),
        courseId,
      };
      const payload = {
        values,
        callback: onCourseMaterialListSuccess,
      };

      courseMaterialListRequest(payload);
    }
  };

  const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  useEffect(() => {
    const coursePermission = getPagePermissions(
      user?.permissions,
      AppPages.CoursesAndMaterials
    );
    const educatorHomePermission = getPagePermissions(
      user?.permissions,
      AppPages.EducatorDashboard,
      EDUCATOR_MODULE_NAME
    );
    if (courseId) {
      if (coursePermission?.view || educatorHomePermission?.view) {
        getCourseMaterialList();
      } else {
        history.goBack();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo]);

  const handleAddEditCourseMaterial = (
    isEdit: boolean,
    data: ICourseMaterial | null
  ) => {
    if (!courseId) {
      toast.error("Please Add and Save the course details first.");

      return;
    }
    setIsEditCourseMaterial(isEdit);
    if (data) {
      setEditData(data);
    } else {
      setEditData(undefined);
    }
    setIsOpenModal(true);
  };

  const handleModalClose = () => {
    setIsOpenModal(false);
    setIsEditCourseMaterial(false);
    setEditData({
      name: "",
      CourseId: 0,
      description: "",
      id: 0,
      link: "",
      type: "",
      document: "",
      fileName: "",
      createdBy: 0,
      createdByName: "",
      updatedBy: 0,
      updatedOn: "",
      createdOn: "",
    });
  };

  const onAddCourseMaterialSuccess = (
    response: AddEditCourseMaterialSuccessPayload
  ) => {
    if (response.success) {
      toast.success(COURSE_MATERIAL_ADD_SUCCESS_MSG);
      handleModalClose();
      getCourseMaterialList();
    } else if (response.message) {
      toast.warning(response.message);
    }
  };

  const handleAddCourseMaterial = (formData: IAddEditDocumentRequest) => {
    const { addCourseMaterialRequest } = props;
    if (addCourseMaterialRequest && courseId) {
      showLoader();
      const payload = {
        values: {
          name: formData?.name.trim(),
          type: formData?.type,
          link: formData?.link?.trim(),
          description: formData?.description?.trim(),
          document: formData?.document,
          courseId,
        },
        callback: onAddCourseMaterialSuccess,
      };
      addCourseMaterialRequest(payload);
    }
  };

  const onEditCourseMaterialSuccess = (
    response: AddEditCourseMaterialSuccessPayload
  ) => {
    if (response.success) {
      toast.success(COURSE_MATERIAL_UPDATE_SUCCESS_MSG);
      handleModalClose();
      getCourseMaterialList();
    } else if (response.message) {
      toast.warning(response.message);
    }
  };

  const handleEditCourseMaterial = (formData: IAddEditDocumentRequest) => {
    const { editCourseMaterialRequest } = props;
    if (editCourseMaterialRequest && courseId) {
      showLoader();
      const payload = {
        values: {
          id: editData?.id,
          courseId,
          name: formData?.name.trim(),
          type: formData?.type,
          link: formData?.link?.trim(),
          description: formData?.description?.trim(),
          document: formData?.document,
          fileName: editData?.fileName,
        },
        callback: onEditCourseMaterialSuccess,
      };
      editCourseMaterialRequest(payload);
    }
  };

  const onDocumentPreviewSuccess = (response: ICourseMaterialPreview) => {
    if (
      response &&
      response.courseMaterialDetails &&
      response.courseMaterialDetails.path
    ) {
      const url = response.courseMaterialDetails.path;
      window.open(url, "_blank");
    }
  };

  const handleDocumentPreview = (id: number) => {
    const { courseMaterialByIdRequest } = props;
    if (courseMaterialByIdRequest) {
      showLoader();
      const payload = {
        values: {
          id: Number(id),
        },
        callback: onDocumentPreviewSuccess,
      };
      courseMaterialByIdRequest(payload);
    }
  };

  const handleDocumentDeleteCloseModal = () => {
    setDeleteDocumentId(0);
    setIsOpenDocumentDeleteModal(false);
  };

  const onDeleteDocumentSuccess = (response: DeleteDocumentSuccessPayload) => {
    if (response.success) {
      toast.success(DOCUMENT_DELETE_SUCCESS_MSG);
      handleDocumentDeleteCloseModal();
      getCourseMaterialList();
    }
  };

  const handleDeleteDocument = () => {
    const { deleteCourseMaterialRequest } = props;

    if (deleteCourseMaterialRequest) {
      showLoader();
      const payload = {
        values: {
          id: Number(deleteDocumentId),
        },
        callback: onDeleteDocumentSuccess,
      };

      deleteCourseMaterialRequest(payload);
    }
  };

  const handleDocumentDeleteModal = (documentId: number) => {
    setDeleteDocumentId(documentId);
    setIsOpenDocumentDeleteModal(true);
  };

  return (
    <Grid item xs={12}>
      <Card>
        <Box className="table-card-header">
          <Box
            sx={{
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
              flexWrap: "wrap",
              rowGap: "16px",
            }}
          >
            <Typography variant="h3">Course Materials</Typography>
            {isAdministrator && (
              <Button
                variant="contained"
                className="btn-add"
                disabled={
                  !getPagePermissions(
                    user?.permissions,
                    AppPages.CoursesAndMaterials
                  )?.add
                }
                onClick={() => {
                  handleAddEditCourseMaterial(false, null);
                }}
              >
                <img src={plusLightIcon} alt="plus" />
                Add
              </Button>
            )}
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  align="left"
                  sx={{
                    width: "232px",
                    minWidth: "232px",
                  }}
                >
                  Document Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "200px", minWidth: "200px" }}
                >
                  File
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "135px", minWidth: "135px" }}
                >
                  Created By
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "116px", minWidth: "116px" }}
                >
                  Created On
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "278px", minWidth: "278px" }}
                >
                  Description
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "114px", minWidth: "114px" }}
                >
                  Updated By
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "114px", minWidth: "114px" }}
                >
                  Updated On
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    width: "118px",
                    [projectTheme.breakpoints.down("sm")]: {
                      width: "112px",
                    },
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courseMaterialList?.map((group) => (
                <>
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      className="doctype-row"
                    >
                      {
                        COURSE_MATERIAL_TYPE.find(
                          (item) => item.value === group.courseMaterialType
                        )?.label
                      }
                    </TableCell>
                  </TableRow>

                  {group.courseMaterials?.map((cm) => (
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Typography variant="body2">{cm.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{cm.fileName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {cm.createdByName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {moment(cm.createdOn).format(DATE_OF_BIRTH_FORMAT)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {cm.description ?? "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {cm.updatedByName || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {cm.updatedOn
                          ? moment(cm.updatedOn).format(DATE_OF_BIRTH_FORMAT)
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        <div className="table-actions">
                          <IconButton
                            onClick={() => handleDocumentPreview(cm.id)}
                          >
                            <img src={documentPreviewIcon} alt="Preview" />
                          </IconButton>
                          {isAdministrator && (
                            <>
                              <IconButton
                                disabled={
                                  !getPagePermissions(
                                    user?.permissions,
                                    AppPages.CoursesAndMaterials
                                  )?.edit
                                }
                                onClick={() =>
                                  handleAddEditCourseMaterial(true, cm)
                                }
                              >
                                <img src={editIcon} alt="edit" />
                              </IconButton>
                              <IconButton
                                disabled={
                                  !getPagePermissions(
                                    user?.permissions,
                                    AppPages.CoursesAndMaterials
                                  )?.delete
                                }
                                onClick={() =>
                                  handleDocumentDeleteModal(Number(cm?.id))
                                }
                              >
                                <img src={deleteIcon} alt="delete" />
                              </IconButton>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {courseMaterialList && courseMaterialList.length > 0 ? (
          <CustomPagination
            handleChange={handleChange}
            page={page}
            pageNo={pageNo}
            onPageChange={onPageChange}
            listMeta={courseMaterialListMeta}
          />
        ) : (
          <NoRecords />
        )}
      </Card>
      <AddEditCourseMaterial
        isEditDocument={isEditCourseMaterial}
        isOpenDocumentModal={isOpenModal}
        handleDocumentClose={handleModalClose}
        handleSaveClick={
          isEditCourseMaterial
            ? handleEditCourseMaterial
            : handleAddCourseMaterial
        }
        editDocumentData={editData}
      />
      <DeleteConfirmationModal
        isOpenDeleteConfirmationModal={isOpenDocumentDeleteModal}
        handleDeleteConfirmationModalClose={handleDocumentDeleteCloseModal}
        deleteConfirmationMessage={DOCUMENT_DELETE_MODAL_MSG}
        handleYesClick={handleDeleteDocument}
      />
    </Grid>
  );
};

export default CourseMaterialsTable;
